import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CameraCapture from '@/components/shared/CameraCapture';
import { Plus, GasPump, Check, Clock, Camera, Speedometer, CaretRight } from '@phosphor-icons/react';

export default function MRExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const month = new Date().toISOString().slice(0, 7);

  const load = () => { api.getExpenses(month).then(r => { setExpenses(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const approved = expenses.filter(e => e.status === 'approved').reduce((s, e) => s + e.amount, 0);
  const pending = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="px-4 pt-4">
        <h2 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
          Fuel & Expenses
        </h2>
        <p className="text-[10px] font-medium" style={{ color: '#8A94A6' }}>
          {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="px-4 grid grid-cols-3 gap-2">
        <div className="p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Total</p>
          <p className="text-lg font-bold mt-1" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{total.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Approved</p>
          <p className="text-lg font-bold mt-1" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{approved.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Pending</p>
          <p className="text-lg font-bold mt-1" style={{ color: '#FFCC00', fontVariantNumeric: 'tabular-nums' }}>₹{pending.toLocaleString()}</p>
        </div>
      </div>

      {/* Add Button */}
      <div className="px-4">
        <Button onClick={() => setShowForm(true)}
          className="w-full h-12 text-sm font-bold rounded-xl"
          style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={18} className="mr-2" /> Log New Expense
        </Button>
      </div>

      {/* Expense List */}
      <div className="px-4 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Recent Expenses</p>
        {loading ? (
          <p className="text-center py-8" style={{ color: '#8A94A6' }}>Loading...</p>
        ) : expenses.length === 0 ? (
          <div className="p-8 text-center rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <GasPump size={40} weight="duotone" style={{ color: '#E2E4E9' }} className="mx-auto mb-2" />
            <p className="text-sm font-medium" style={{ color: '#8A94A6' }}>No expenses logged yet</p>
          </div>
        ) : expenses.map((e, i) => (
          <div key={i} className="p-3.5 rounded-xl border flex items-center gap-3" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: e.expense_type === 'fuel' ? '#FFCC0010' : '#007AFF10' }}>
              {e.expense_type === 'fuel' ?
                <GasPump size={20} weight="duotone" style={{ color: '#FFCC00' }} /> :
                <Speedometer size={20} weight="duotone" style={{ color: '#007AFF' }} />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: '#0A2540' }}>
                {e.expense_type === 'fuel' ? 'Fuel Expense' : e.expense_type}
              </p>
              <p className="text-[10px]" style={{ color: '#8A94A6' }}>
                {e.date} {e.distance_km ? `| ${e.distance_km} km` : ''}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold" style={{ fontVariantNumeric: 'tabular-nums', color: '#0A2540' }}>₹{e.amount}</p>
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase"
                style={{ color: e.status === 'approved' ? '#34C759' : '#FFCC00' }}>
                {e.status === 'approved' ? <Check size={10} /> : <Clock size={10} />}
                {e.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Expense Form Dialog */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-sm p-5">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Log Fuel Expense</DialogTitle>
          </DialogHeader>
          <ExpenseForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseForm({ onSave }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    expense_type: 'fuel',
    start_reading: '',
    end_reading: '',
    amount: 0,
    notes: ''
  });
  const [startPhoto, setStartPhoto] = useState(null);
  const [endPhoto, setEndPhoto] = useState(null);
  const [showStartCam, setShowStartCam] = useState(false);
  const [showEndCam, setShowEndCam] = useState(false);
  const [saving, setSaving] = useState(false);

  const distance = form.start_reading && form.end_reading ? Number(form.end_reading) - Number(form.start_reading) : 0;
  const estAmount = distance * 5;

  const save = async () => {
    setSaving(true);
    try {
      await api.createExpense({
        ...form,
        start_reading: Number(form.start_reading) || null,
        end_reading: Number(form.end_reading) || null,
        distance_km: distance,
        amount: estAmount || form.amount
      });
      onSave();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Date</Label>
        <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-10 mt-1 rounded-lg" />
      </div>

      {/* Start Meter Reading */}
      <div className="p-3 rounded-xl" style={{ background: '#F5F6F8' }}>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Start Meter Reading</Label>
        <div className="flex gap-2 mt-2">
          <Input type="number" value={form.start_reading} onChange={e => setForm(f => ({ ...f, start_reading: e.target.value }))}
            className="h-10 flex-1 rounded-lg" placeholder="e.g. 12340" />
          <Button onClick={() => setShowStartCam(true)} variant="outline" className="h-10 px-3 rounded-lg shrink-0" style={{ borderColor: '#E2E4E9' }}>
            <Camera size={16} style={{ color: '#007AFF' }} />
          </Button>
        </div>
        {startPhoto && (
          <img src={startPhoto} alt="Start meter" className="w-full h-20 object-cover rounded-lg mt-2" />
        )}
      </div>

      {/* End Meter Reading */}
      <div className="p-3 rounded-xl" style={{ background: '#F5F6F8' }}>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>End Meter Reading</Label>
        <div className="flex gap-2 mt-2">
          <Input type="number" value={form.end_reading} onChange={e => setForm(f => ({ ...f, end_reading: e.target.value }))}
            className="h-10 flex-1 rounded-lg" placeholder="e.g. 12380" />
          <Button onClick={() => setShowEndCam(true)} variant="outline" className="h-10 px-3 rounded-lg shrink-0" style={{ borderColor: '#E2E4E9' }}>
            <Camera size={16} style={{ color: '#007AFF' }} />
          </Button>
        </div>
        {endPhoto && (
          <img src={endPhoto} alt="End meter" className="w-full h-20 object-cover rounded-lg mt-2" />
        )}
      </div>

      {/* Distance & Auto Calc */}
      {distance > 0 && (
        <div className="p-3 rounded-xl flex items-center justify-between" style={{ background: '#007AFF08', border: '1px solid #007AFF20' }}>
          <div>
            <p className="text-xs font-bold" style={{ color: '#007AFF' }}>Auto-calculated</p>
            <p className="text-[10px]" style={{ color: '#8A94A6' }}>Distance: {distance} km @ ₹5/km</p>
          </div>
          <p className="text-lg font-extrabold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{estAmount}</p>
        </div>
      )}

      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Notes</Label>
        <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-10 mt-1 rounded-lg" placeholder="Optional" />
      </div>

      <Button onClick={save} disabled={saving} className="w-full h-12 text-sm font-bold rounded-xl" style={{ background: '#0A2540', color: 'white' }}>
        {saving ? 'Submitting...' : 'Submit Expense'}
      </Button>

      {/* Camera Dialogs */}
      <Dialog open={showStartCam} onOpenChange={() => setShowStartCam(false)}>
        <DialogContent className="max-w-sm p-4">
          <DialogHeader><DialogTitle>Start Meter Photo</DialogTitle></DialogHeader>
          <CameraCapture facing="environment" label="Take photo of start meter reading"
            onCapture={(d) => { setStartPhoto(d); setShowStartCam(false); }}
            onCancel={() => setShowStartCam(false)} />
        </DialogContent>
      </Dialog>
      <Dialog open={showEndCam} onOpenChange={() => setShowEndCam(false)}>
        <DialogContent className="max-w-sm p-4">
          <DialogHeader><DialogTitle>End Meter Photo</DialogTitle></DialogHeader>
          <CameraCapture facing="environment" label="Take photo of end meter reading"
            onCapture={(d) => { setEndPhoto(d); setShowEndCam(false); }}
            onCancel={() => setShowEndCam(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
